import { useRef } from "react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "@/lib/utils";
import { DURATION, EASE_SIGNATURE } from "./motion";

interface PillTab {
  id: string;
  label: string;
  disabled?: boolean;
}

interface PillTabsProps {
  tabs: PillTab[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
}

/**
 * Rounded pill-track tab bar with a motion sliding highlight.
 * Keyboard: ArrowLeft / ArrowRight move focus and selection across enabled tabs only.
 */
export function PillTabs({ tabs, value, onChange, className }: PillTabsProps) {
  const reduced = useReducedMotion();
  const listRef = useRef<HTMLDivElement>(null);

  const enabledTabs = tabs.filter((t) => !t.disabled);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, currentId: string) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();

    const currentIndex = enabledTabs.findIndex((t) => t.id === currentId);
    if (currentIndex === -1) return;

    const direction = e.key === "ArrowRight" ? 1 : -1;
    const next = enabledTabs[(currentIndex + direction + enabledTabs.length) % enabledTabs.length];
    if (!next) return;

    onChange(next.id);
    // Move DOM focus to the newly selected tab button.
    const el = listRef.current?.querySelector<HTMLButtonElement>(`[data-tab-id="${next.id}"]`);
    el?.focus();
  };

  return (
    <div
      data-slot="pill-tabs"
      ref={listRef}
      role="tablist"
      aria-label="Navigation tabs"
      className={cn("inline-flex h-11 items-center gap-1 rounded-full bg-muted p-1", className)}
    >
      {tabs.map((tab) => {
        const selected = tab.id === value;
        const isEnabled = !tab.disabled;

        return (
          <button
            key={tab.id}
            data-tab-id={tab.id}
            role="tab"
            aria-selected={selected}
            aria-disabled={tab.disabled}
            tabIndex={selected ? 0 : -1}
            disabled={tab.disabled}
            title={tab.disabled ? "Coming soon" : undefined}
            onClick={() => {
              if (isEnabled) onChange(tab.id);
            }}
            onKeyDown={(e) => {
              if (isEnabled) handleKeyDown(e, tab.id);
            }}
            className={cn(
              "relative z-10 h-9 min-w-[4rem] flex-1 rounded-full px-4 text-sm font-medium transition-colors outline-none",
              "focus-visible:ring-[3px] focus-visible:ring-ring/50",
              selected ? "text-foreground" : "text-muted-foreground hover:text-foreground/80",
              tab.disabled && "cursor-not-allowed opacity-50",
            )}
          >
            {/* Sliding pill highlight underneath the label */}
            {selected && (
              <motion.span
                layoutId="pill-tabs-highlight"
                data-slot="pill-tabs-highlight"
                className="absolute inset-0 rounded-full bg-background shadow-tile"
                transition={
                  reduced ? { duration: 0 } : { duration: DURATION.base, ease: EASE_SIGNATURE }
                }
              />
            )}
            <span className="relative">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}
